import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
export const api = axios.create({ baseURL: API, withCredentials: true });

// ---------- Auth token storage ----------
// On iOS Safari (and other browsers with strict ITP), HttpOnly cookies set by
// the API can be silently dropped on subsequent requests. To keep the user
// signed-in robustly across all browsers, we ALSO persist the JWT in
// localStorage and send it as `Authorization: Bearer <token>`. The backend
// accepts either auth method.
const TOKEN_KEY = "falkedeck.auth_token";

function getStoredToken() {
  try { return typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null; }
  catch { return null; }
}
function setStoredToken(token) {
  try {
    if (typeof window === "undefined") return;
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode / quota — ignore */ }
}

// Public helper for pages that authenticate the user (reset-password, invite accept)
// without going through the AuthProvider.login() flow.
export const setAuthToken = setStoredToken;

// Always attach Authorization header if we have a stored token
api.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ---------- Auth ----------
const AuthCtx = createContext(null);

// Idle / inactivity timeout — user is logged out after this many ms of no
// activity. 8h covers a typical work day; longer is unsafe on shared/mobile
// devices.
const IDLE_LOGOUT_MS = 8 * 60 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "visibilitychange"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); }
    catch { setUser(false); }
    finally { setReady(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };
  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setStoredToken(null);
    setUser(false);
  }, []);

  // 401 interceptor — when any authenticated API call fails with 401 (e.g.
  // expired session, browser dropped the cookie, server restart), force the
  // client to logout instead of silently rendering empty lists.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const url = (err?.config?.url || "");
        // Don't trigger logout on the auth probe itself or on the login attempt.
        const isAuthProbe = url.endsWith("/auth/me") || url.endsWith("/auth/login");
        if (status === 401 && !isAuthProbe) {
          setStoredToken(null);
          setUser(false);
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            window.location.assign("/login");
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  // Idle auto-logout — only active when user is authenticated.
  useEffect(() => {
    if (!user) return undefined;
    let timer = null;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, IDLE_LOGOUT_MS);
    };
    const onActivity = () => arm();
    arm();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => {
      if (timer) clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [user, logout]);

  const value = useMemo(() => ({ user, ready, login, register, logout, refresh }), [user, ready, refresh, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);

// ---------- Permissions helpers ----------
// Mirror of backend permissions.can() for UI gating.
export function usePerm() {
  const { user } = useAuth();
  const role = user?.role || "user";
  const perms = user?.permissions || {};

  const isAdmin = role === "admin";
  const isExpert = role === "expert";
  const isPrivileged = isAdmin || isExpert;

  const level = (key) => (isPrivileged ? "full" : (perms[key] || "none"));

  const can = (key, action = "view") => {
    if (isPrivileged) return true;
    const lv = level(key);
    if (lv === "none") return false;
    if (lv === "full") return true;
    if (key === "positions" || key === "quotes") return action === "view";
    if (key === "categories") return false;
    if (key === "aufmasse") {
      // levels: full | view | none
      if (lv === "view") return action === "view";
      return false;
    }
    if (key === "customers") {
      if (lv === "edit_no_delete") return ["view", "edit", "create"].includes(action);
      if (lv === "view_notes_only") return action === "view" || action === "edit_notes";
      return false;
    }
    if (key === "invoices") {
      if (lv === "view_download") return ["view", "export"].includes(action);
      return false;
    }
    if (key === "settings") return false;
    return false;
  };

  const visible = (key) => can(key, "view");
  const hidesPrices = (key) => level(key) === "view_no_price";
  const tabHidden = {
    dashboard: !isPrivileged && (role === "user" || role === "handworker"),
    categories: role === "handworker" || (!isPrivileged && level("categories") === "none"),
    settings: !isPrivileged && level("settings") === "none",
    invoices: role === "handworker" || (!isPrivileged && level("invoices") === "none"),
    positions: !isPrivileged && level("positions") === "none",
    quotes: !isPrivileged && level("quotes") === "none",
    customers: !isPrivileged && level("customers") === "none",
    users: !isPrivileged,  // Nutzer-Reiter only for admin/expert
  };

  return { user, role, isAdmin, isExpert, isPrivileged, level, can, visible, hidesPrices, tabHidden };
}

// ---------- Language ----------
const LangCtx = createContext(null);
export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "de");
  const toggleLang = () => { const next = lang === "de" ? "en" : "de"; setLang(next); localStorage.setItem("lang", next); };
  const t = useCallback((key) => translations[lang]?.[key] ?? key, [lang]);
  const value = useMemo(() => ({ lang, setLang, toggleLang, t }), [lang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}
export const useLang = () => useContext(LangCtx);

// ---------- Theme (light/dark) ----------
const ThemeCtx = createContext(null);
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}
export const useTheme = () => useContext(ThemeCtx);

// ---------- Units ----------
const UnitsCtx = createContext({ units: [] });
export function UnitsProvider({ children }) {
  const [units, setUnits] = useState([]);
  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data)).catch(() => {});
  }, []);
  const byValue = useMemo(() => Object.fromEntries(units.map((u) => [u.value, u])), [units]);
  const value = useMemo(() => ({ units, byValue }), [units, byValue]);
  return <UnitsCtx.Provider value={value}>{children}</UnitsCtx.Provider>;
}
export const useUnits = () => useContext(UnitsCtx);

// ---------- Helpers ----------
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof detail === "object" && detail?.msg) return detail.msg;
  return String(detail);
}
export function formatCurrency(value, lang = "de") {
  const n = Number(value || 0);
  return new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", {
    style: "currency", currency: "EUR", maximumFractionDigits: 2,
  }).format(n);
}


const PIECE_UNITS = new Set(["stk", "stück", "stueck", "pcs", "pc", "piece", "pieces", "ea"]);

/** Format a quantity. Piece units (Stk/pcs) are shown WITHOUT decimals. */
export function formatQuantity(value, unit = "", lang = "de") {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  const u = (unit || "").trim().toLowerCase();
  if (PIECE_UNITS.has(u)) return String(Math.round(n));
  return n.toLocaleString(lang === "de" ? "de-DE" : "en-US", { maximumFractionDigits: 2 });
}
